import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, LogOut, Upload, Trash2, Plus } from "lucide-react";
import { api } from "@/lib/api";

const TABS = ["OVERVIEW", "SALES", "FANS", "INBOX", "DEMOS", "BOOKINGS", "ROSTER", "RELEASES", "MERCH", "MEDIA", "SHOWS"];

export default function Admin() {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("OVERVIEW");
  const [overview, setOverview] = useState(null);
  const [orders, setOrders] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [members, setMembers] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [products, setProducts] = useState([]);
  const [shows, setShows] = useState([]);
  const [showForm, setShowForm] = useState({ date: "", city: "", venue: "", status: "TICKETS", ticket_url: "", time: "" });
  const [demos, setDemos] = useState([]);
  const [artists, setArtists] = useState([]);
  const [artistForm, setArtistForm] = useState({ name: "", handle: "", role: "ARTIST", bio: "", link: "" });
  const [bookings, setBookings] = useState([]);
  const [releasesList, setReleasesList] = useState([]);
  const [releaseForm, setReleaseForm] = useState({ title: "", kind: "SINGLE", date: "", note: "" });
  const [introInfo, setIntroInfo] = useState(null);
  const [productForm, setProductForm] = useState({ name: "", price: "", tag: "" });
  const [editingProduct, setEditingProduct] = useState(null);
  const [messages, setMessages] = useState([]);
  const [mediaArtists, setMediaArtists] = useState([]);
  const [sizeStock, setSizeStock] = useState({ S: "", M: "", L: "", XL: "", XXL: "" });
  const [liveInfo, setLiveInfo] = useState(null);
  const [galleryPhotos, setGalleryPhotos] = useState([]);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) loadTab(tab);
  }, [user, tab]);

  const loadTab = async (t) => {
    try {
      if (t === "OVERVIEW") setOverview((await api.get("/admin/overview")).data);
      if (t === "SALES") setOrders((await api.get("/admin/orders")).data);
      if (t === "FANS") {
        setSubscribers((await api.get("/admin/subscribers")).data);
        setMembers((await api.get("/admin/members")).data);
      }
      if (t === "MEDIA") {
        setTracks((await api.get("/tracks")).data);
        setProducts((await api.get("/products")).data);
        setIntroInfo((await api.get("/settings/intro")).data);
        setMediaArtists((await api.get("/artists")).data);
        setLiveInfo((await api.get("/settings/live")).data);
        setGalleryPhotos((await api.get("/gallery")).data);
      }
      if (t === "MERCH") setProducts((await api.get("/products")).data);
      if (t === "INBOX") setMessages((await api.get("/admin/messages")).data);
      if (t === "SHOWS") setShows((await api.get("/shows")).data);
      if (t === "DEMOS") setDemos((await api.get("/admin/demos")).data);
      if (t === "ROSTER") setArtists((await api.get("/artists")).data);
      if (t === "BOOKINGS") setBookings((await api.get("/admin/bookings")).data);
      if (t === "RELEASES") setReleasesList((await api.get("/releases")).data);
    } catch {
      toast.error("Failed to load data");
    }
  };

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  const upload = async (file, url) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(url, fd);
      toast.success("Uploaded — live on the site now");
      loadTab(tab);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed");
    }
  };

  const addShow = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/shows", showForm);
      toast.success("Show added");
      setShowForm({ date: "", city: "", venue: "", status: "TICKETS", ticket_url: "", time: "" });
      loadTab("SHOWS");
    } catch {
      toast.error("Failed to add show");
    }
  };

  const delShow = async (id) => {
    await api.delete(`/admin/shows/${id}`).catch(() => {});
    loadTab("SHOWS");
  };

  if (user === null) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <Loader2 className="animate-spin text-primary" size={32} />
      </main>
    );
  }

  if (user === false) {
    return (
      <main data-testid="admin-login-page" className="flex min-h-screen items-center justify-center px-6">
        <form
          data-testid="admin-login-form"
          onSubmit={login}
          className="w-full max-w-md border border-white/10 bg-card p-10"
        >
          <p className="font-mono text-xs tracking-[0.4em] text-primary">RESTRICTED</p>
          <h1 className="mt-3 font-anton text-4xl text-white">MILLZZ HQ</h1>
          <div className="mt-8 space-y-6">
            <div>
              <label htmlFor="admin-email" className="font-mono text-[10px] tracking-[0.3em] text-white/50">EMAIL</label>
              <input
                id="admin-email"
                data-testid="admin-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full border-b border-white/20 bg-transparent py-3 text-white outline-none transition-colors duration-300 focus:border-primary"
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="font-mono text-[10px] tracking-[0.3em] text-white/50">PASSWORD</label>
              <input
                id="admin-password"
                data-testid="admin-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full border-b border-white/20 bg-transparent py-3 text-white outline-none transition-colors duration-300 focus:border-primary"
              />
            </div>
            {error && <p data-testid="admin-login-error" className="font-mono text-xs text-red-400">{error}</p>}
            <button
              type="submit"
              data-testid="admin-login-button"
              disabled={loading}
              className="flex w-full items-center justify-center gap-3 bg-primary px-8 py-4 font-mono text-sm font-bold tracking-[0.2em] text-black transition-transform duration-300 hover:-translate-y-1 disabled:opacity-50"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {loading ? "ENTERING..." : "ENTER HQ"}
            </button>
          </div>
        </form>
      </main>
    );
  }

  return (
    <main data-testid="admin-dashboard" className="mx-auto max-w-7xl px-6 pb-40 pt-36">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-mono text-xs tracking-[0.4em] text-primary">MILLZZ HQ</p>
          <h1 data-testid="admin-title" className="mt-2 font-anton text-5xl text-white">DASHBOARD</h1>
        </div>
        <button
          data-testid="admin-logout-button"
          onClick={logout}
          className="flex items-center gap-2 border border-white/25 px-5 py-3 font-mono text-xs tracking-[0.2em] text-white/70 transition-colors duration-300 hover:border-primary hover:text-primary"
        >
          <LogOut size={14} /> LOG OUT
        </button>
      </div>

      <div data-testid="admin-tabs" className="mt-10 flex flex-wrap gap-2 border-b border-white/10 pb-4">
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`admin-tab-${t.toLowerCase()}`}
            onClick={() => setTab(t)}
            className={`px-5 py-2 font-mono text-xs tracking-[0.25em] transition-colors duration-300 ${
              tab === t ? "bg-primary text-black" : "text-white/50 hover:text-white"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="mt-10">
        {tab === "OVERVIEW" && overview && (
          <div data-testid="admin-overview" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {[
              ["REVENUE", `$${overview.revenue.toFixed(2)}`],
              ["PAID ORDERS", overview.orders],
              ["SUBSCRIBERS", overview.subscribers],
              ["CIRCLE MEMBERS", overview.members],
              ["SHOWS LISTED", overview.shows],
            ].map(([label, value]) => (
              <div key={label} data-testid={`admin-stat-${label.toLowerCase().replace(/\s/g, "-")}`} className="border border-white/10 bg-card p-6">
                <p className="font-anton text-3xl text-primary">{value}</p>
                <p className="mt-2 font-mono text-[10px] tracking-[0.25em] text-white/50">{label}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "SALES" && (
          <div data-testid="admin-orders-table" className="overflow-x-auto border border-white/10">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/10 font-mono text-[10px] tracking-[0.25em] text-white/40">
                  <th className="p-4">ITEM</th><th className="p-4">AMOUNT</th><th className="p-4">STATUS</th><th className="p-4">PAYMENT</th><th className="p-4">DATE</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.session_id} data-testid={`admin-order-${o.session_id.slice(-8)}`} className="border-b border-white/5 text-sm text-white/70">
                    <td className="p-4 font-mono text-xs">{o.lookup_key}</td>
                    <td className="p-4 text-primary">${o.amount.toFixed(2)}</td>
                    <td className="p-4 font-mono text-xs">{o.status}</td>
                    <td className={`p-4 font-mono text-xs ${o.payment_status === "paid" ? "text-primary" : "text-white/40"}`}>{o.payment_status}</td>
                    <td className="p-4 font-mono text-xs text-white/40">{o.created_at}</td>
                  </tr>
                ))}
                {orders.length === 0 && (
                  <tr><td colSpan={5} className="p-10 text-center font-mono text-xs text-white/40">NO ORDERS YET</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === "FANS" && (
          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <h2 className="mb-4 font-oswald text-xl font-bold tracking-wider text-white">NEWSLETTER SUBSCRIBERS ({subscribers.length})</h2>
              <div data-testid="admin-subscribers-list" className="border border-white/10">
                {subscribers.map((s) => (
                  <div key={s.id} className="flex justify-between border-b border-white/5 p-4 text-sm">
                    <span className="text-white/80">{s.email}</span>
                    <span className="font-mono text-xs text-white/40">{s.name || "—"}</span>
                  </div>
                ))}
                {subscribers.length === 0 && <p className="p-8 text-center font-mono text-xs text-white/40">NO SUBSCRIBERS YET</p>}
              </div>
            </div>
            <div>
              <h2 className="mb-4 font-oswald text-xl font-bold tracking-wider text-white">CIRCLE MEMBERS ({members.length})</h2>
              <div data-testid="admin-members-list" className="border border-primary/30">
                {members.map((m) => (
                  <div key={m.session_id} className="flex justify-between border-b border-white/5 p-4 text-sm">
                    <span className="font-mono text-xs text-white/80">{m.lookup_key}</span>
                    <span className="text-primary">${m.amount.toFixed(2)}</span>
                  </div>
                ))}
                {members.length === 0 && <p className="p-8 text-center font-mono text-xs text-white/40">NO PAID MEMBERS YET</p>}
              </div>
            </div>
          </div>
        )}

        {tab === "DEMOS" && (
          <div data-testid="admin-demos" className="border border-white/10">
            {demos.map((d) => (
              <div key={d.id} data-testid={`admin-demo-${d.id.slice(0, 8)}`} className="flex flex-wrap items-center gap-4 border-b border-white/5 p-4">
                <div className="min-w-0 flex-1">
                  <p className="font-oswald font-bold tracking-wider text-white">{d.artist_name}</p>
                  <p className="font-mono text-xs text-white/50">{d.email}</p>
                  {d.message && <p className="mt-1 text-sm text-white/60">{d.message}</p>}
                  <p className="mt-1 font-mono text-[10px] text-white/30">{String(d.created_at).slice(0, 10)}</p>
                </div>
                <a href={d.link} target="_blank" rel="noreferrer" data-testid={`demo-listen-${d.id.slice(0, 8)}`}
                  className="border border-primary/40 px-4 py-2 font-mono text-[10px] font-bold tracking-[0.2em] text-primary transition-colors duration-300 hover:bg-primary hover:text-black">
                  LISTEN
                </a>
                <button data-testid={`demo-delete-${d.id.slice(0, 8)}`} aria-label={`Delete demo from ${d.artist_name}`}
                  onClick={async () => { await api.delete(`/admin/demos/${d.id}`).catch(() => {}); loadTab("DEMOS"); }}
                  className="p-2 text-white/40 transition-colors duration-300 hover:text-red-400">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            {demos.length === 0 && <p className="p-10 text-center font-mono text-xs text-white/40">NO DEMOS YET — SHARE THE LABEL PAGE</p>}
          </div>
        )}

        {tab === "BOOKINGS" && (
          <div data-testid="admin-bookings" className="border border-white/10">
            {bookings.map((b) => (
              <div key={b.id} data-testid={`admin-booking-${b.id.slice(0, 8)}`} className="flex flex-wrap items-center gap-4 border-b border-white/5 p-4">
                <span className="bg-primary/15 px-3 py-1 font-mono text-[10px] tracking-[0.25em] text-primary">{b.event_type}</span>
                <div className="min-w-0 flex-1">
                  <p className="font-oswald font-bold tracking-wider text-white">{b.name} — {b.city}</p>
                  <p className="font-mono text-xs text-white/50">{b.email} · {b.date}</p>
                  {b.message && <p className="mt-1 text-sm text-white/60">{b.message}</p>}
                </div>
                <button data-testid={`booking-confirm-${b.id.slice(0, 8)}`}
                  onClick={async () => { await api.post(`/admin/bookings/${b.id}/confirm`, { confirmed: !b.confirmed }).catch(() => {}); loadTab("BOOKINGS"); }}
                  className={`px-3 py-2 font-mono text-[10px] font-bold tracking-[0.2em] transition-colors duration-300 ${b.confirmed ? "bg-primary text-black" : "border border-white/25 text-white/70 hover:border-primary hover:text-primary"}`}>
                  {b.confirmed ? "CONFIRMED" : "CONFIRM"}
                </button>
                <button data-testid={`booking-delete-${b.id.slice(0, 8)}`} aria-label={`Delete booking from ${b.name}`}
                  onClick={async () => { await api.delete(`/admin/bookings/${b.id}`).catch(() => {}); loadTab("BOOKINGS"); }}
                  className="p-2 text-white/40 transition-colors duration-300 hover:text-red-400">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            {bookings.length === 0 && <p className="p-10 text-center font-mono text-xs text-white/40">NO BOOKING REQUESTS YET</p>}
          </div>
        )}

        {tab === "RELEASES" && (
          <div data-testid="admin-releases" className="grid gap-10 lg:grid-cols-2">
            <form data-testid="admin-release-form" className="space-y-5 border border-white/10 bg-card p-8"
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await api.post("/admin/releases", releaseForm);
                  toast.success("Release date set — showing as DROPPING NEXT");
                  setReleaseForm({ title: "", kind: "SINGLE", date: "", note: "" });
                  loadTab("RELEASES");
                } catch { toast.error("Failed to add release"); }
              }}>
              <h2 className="font-oswald text-xl font-bold tracking-wider text-white">SET A RELEASE DATE</h2>
              <input data-testid="release-title-input" placeholder="RELEASE TITLE" required value={releaseForm.title}
                onChange={(e) => setReleaseForm({ ...releaseForm, title: e.target.value.toUpperCase() })}
                className="w-full border-b border-white/20 bg-transparent py-3 text-white outline-none placeholder:text-white/25 focus:border-primary" />
              <select data-testid="release-kind-select" value={releaseForm.kind}
                onChange={(e) => setReleaseForm({ ...releaseForm, kind: e.target.value })}
                className="w-full border-b border-white/20 bg-transparent py-3 text-white outline-none focus:border-primary">
                {["SINGLE", "EP", "ALBUM", "VIDEO", "MIXTAPE"].map((k) => (
                  <option key={k} value={k} className="bg-black">{k}</option>
                ))}
              </select>
              <input data-testid="release-date-input" type="date" required value={releaseForm.date}
                onChange={(e) => setReleaseForm({ ...releaseForm, date: e.target.value })}
                className="w-full border-b border-white/20 bg-transparent py-3 text-white outline-none [color-scheme:dark] focus:border-primary" />
              <input data-testid="release-note-input" placeholder="Note (optional, e.g. feat. ...)" value={releaseForm.note}
                onChange={(e) => setReleaseForm({ ...releaseForm, note: e.target.value })}
                className="w-full border-b border-white/20 bg-transparent py-3 text-white outline-none placeholder:text-white/25 focus:border-primary" />
              <button type="submit" data-testid="release-add-button"
                className="flex items-center gap-2 bg-primary px-6 py-3 font-mono text-xs font-bold tracking-[0.2em] text-black transition-transform duration-300 hover:-translate-y-1">
                <Plus size={14} /> ANNOUNCE RELEASE
              </button>
            </form>
            <div>
              {releasesList.map((r) => (
                <div key={r.id} data-testid={`admin-release-row-${r.id.slice(0, 8)}`} className="mb-3 flex items-center gap-4 border border-white/10 p-4">
                  <span className="font-mono text-xs text-primary">{r.date}</span>
                  <div className="flex-1">
                    <p className="font-oswald font-bold tracking-wider text-white">{r.title}</p>
                    <p className="font-mono text-[10px] text-white/40">{r.kind}{r.note ? ` — ${r.note}` : ""}</p>
                  </div>
                  <button data-testid={`release-delete-${r.id.slice(0, 8)}`} aria-label={`Delete ${r.title}`}
                    onClick={async () => { await api.delete(`/admin/releases/${r.id}`).catch(() => {}); loadTab("RELEASES"); }}
                    className="p-2 text-white/40 transition-colors duration-300 hover:text-red-400">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              {releasesList.length === 0 && <p className="p-10 text-center font-mono text-xs text-white/40">NO UPCOMING RELEASES ANNOUNCED</p>}
            </div>
          </div>
        )}

        {tab === "ROSTER" && (
          <div data-testid="admin-roster" className="grid gap-10 lg:grid-cols-2">
            <form data-testid="admin-artist-form" className="space-y-5 border border-white/10 bg-card p-8"
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  await api.post("/admin/artists", artistForm);
                  toast.success("Artist added to the roster");
                  setArtistForm({ name: "", handle: "", role: "ARTIST", bio: "", link: "" });
                  loadTab("ROSTER");
                } catch { toast.error("Failed to add artist"); }
              }}>
              <h2 className="font-oswald text-xl font-bold tracking-wider text-white">ADD AN ARTIST</h2>
              <input data-testid="artist-name-input" placeholder="ARTIST NAME" required value={artistForm.name}
                onChange={(e) => setArtistForm({ ...artistForm, name: e.target.value.toUpperCase() })}
                className="w-full border-b border-white/20 bg-transparent py-3 text-white outline-none placeholder:text-white/25 focus:border-primary" />
              <input data-testid="artist-handle-input" placeholder="@handle" value={artistForm.handle}
                onChange={(e) => setArtistForm({ ...artistForm, handle: e.target.value })}
                className="w-full border-b border-white/20 bg-transparent py-3 text-white outline-none placeholder:text-white/25 focus:border-primary" />
              <input data-testid="artist-role-input" placeholder="ROLE (e.g. PRODUCER, ARTIST)" value={artistForm.role}
                onChange={(e) => setArtistForm({ ...artistForm, role: e.target.value.toUpperCase() })}
                className="w-full border-b border-white/20 bg-transparent py-3 text-white outline-none placeholder:text-white/25 focus:border-primary" />
              <textarea data-testid="artist-bio-input" placeholder="Short bio" rows={3} value={artistForm.bio}
                onChange={(e) => setArtistForm({ ...artistForm, bio: e.target.value })}
                className="w-full resize-none border-b border-white/20 bg-transparent py-3 text-white outline-none placeholder:text-white/25 focus:border-primary" />
              <input data-testid="artist-link-input" placeholder="Profile link (optional, e.g. /about)" value={artistForm.link}
                onChange={(e) => setArtistForm({ ...artistForm, link: e.target.value })}
                className="w-full border-b border-white/20 bg-transparent py-3 text-white outline-none placeholder:text-white/25 focus:border-primary" />
              <button type="submit" data-testid="artist-add-button"
                className="flex items-center gap-2 bg-primary px-6 py-3 font-mono text-xs font-bold tracking-[0.2em] text-black transition-transform duration-300 hover:-translate-y-1">
                <Plus size={14} /> ADD ARTIST
              </button>
            </form>
            <div>
              {artists.map((a) => (
                <div key={a.id} data-testid={`admin-artist-row-${a.id}`} className="mb-3 flex items-center gap-4 border border-white/10 p-4">
                  {a.photo ? (
                    <img src={a.photo} alt={a.name} className="h-12 w-12 border border-white/15 object-cover" />
                  ) : (
                    <div className="flex h-12 w-12 items-center justify-center border border-white/15 bg-black font-anton text-lg text-primary">{a.name.charAt(0)}</div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-oswald font-bold tracking-wider text-white">{a.name}</p>
                    <p className="font-mono text-[10px] text-white/40">{a.role}</p>
                  </div>
                  <label data-testid={`upload-artist-${a.id}`} className="flex cursor-pointer items-center gap-2 border border-white/25 px-3 py-2 font-mono text-[10px] tracking-[0.2em] text-white transition-colors duration-300 hover:border-primary hover:text-primary">
                    <Upload size={12} /> PHOTO
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0], `/admin/upload/artist/${a.id}`)} />
                  </label>
                  <label data-testid={`gallery-artist-${a.id}`} className="flex cursor-pointer items-center gap-2 border border-white/25 px-3 py-2 font-mono text-[10px] tracking-[0.2em] text-white transition-colors duration-300 hover:border-primary hover:text-primary">
                    <Upload size={12} /> GALLERY{a.photos?.length ? ` (${a.photos.length})` : ""}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0], `/admin/upload/artist-gallery/${a.id}`)} />
                  </label>
                  <button data-testid={`artist-delete-${a.id}`} aria-label={`Delete ${a.name}`}
                    onClick={async () => { await api.delete(`/admin/artists/${a.id}`).catch(() => {}); loadTab("ROSTER"); }}
                    className="p-2 text-white/40 transition-colors duration-300 hover:text-red-400">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "MERCH" && (
          <div data-testid="admin-merch" className="grid gap-10 lg:grid-cols-2">
            <form data-testid="admin-product-form" className="space-y-5 border border-white/10 bg-card p-8"
              onSubmit={async (e) => {
                e.preventDefault();
                const payload = {
                  name: productForm.name,
                  price: parseFloat(productForm.price),
                  tag: productForm.tag,
                  sizes: Object.entries(sizeStock)
                    .filter(([, v]) => v !== "")
                    .map(([size, v]) => ({ size, stock: parseInt(v) || 0 })),
                };
                try {
                  if (editingProduct) {
                    await api.put(`/admin/products/${editingProduct}`, payload);
                    toast.success("Product updated — new price is live at checkout");
                  } else {
                    await api.post("/admin/products", payload);
                    toast.success("Product added to the shop");
                  }
                  setProductForm({ name: "", price: "", tag: "" });
                  setSizeStock({ S: "", M: "", L: "", XL: "", XXL: "" });
                  setEditingProduct(null);
                  loadTab("MERCH");
                } catch { toast.error("Save failed"); }
              }}>
              <h2 className="font-oswald text-xl font-bold tracking-wider text-white">
                {editingProduct ? "EDIT PRODUCT" : "ADD A PRODUCT"}
              </h2>
              <input data-testid="product-name-input" placeholder="PRODUCT NAME" required value={productForm.name}
                onChange={(e) => setProductForm({ ...productForm, name: e.target.value.toUpperCase() })}
                className="w-full border-b border-white/20 bg-transparent py-3 text-white outline-none placeholder:text-white/25 focus:border-primary" />
              <input data-testid="product-price-input" type="number" step="0.01" min="0.5" placeholder="Price (USD)" required value={productForm.price}
                onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                className="w-full border-b border-white/20 bg-transparent py-3 text-white outline-none placeholder:text-white/25 focus:border-primary" />
              <input data-testid="product-tag-input" placeholder="Tag (e.g. HEAVYWEIGHT, LIMITED)" value={productForm.tag}
                onChange={(e) => setProductForm({ ...productForm, tag: e.target.value.toUpperCase() })}
                className="w-full border-b border-white/20 bg-transparent py-3 text-white outline-none placeholder:text-white/25 focus:border-primary" />
              <div data-testid="product-sizes-editor">
                <p className="font-mono text-[10px] tracking-[0.3em] text-white/50">SIZES & STOCK — LEAVE BLANK IF NOT OFFERED, 0 = SOLD OUT</p>
                <div className="mt-3 grid grid-cols-5 gap-2">
                  {["S", "M", "L", "XL", "XXL"].map((s) => (
                    <div key={s}>
                      <p className="text-center font-mono text-[10px] text-white/50">{s}</p>
                      <input data-testid={`size-stock-${s.toLowerCase()}`} type="number" min="0" value={sizeStock[s]}
                        onChange={(e) => setSizeStock({ ...sizeStock, [s]: e.target.value })}
                        className="mt-1 w-full border border-white/20 bg-transparent px-2 py-2 text-center text-white outline-none focus:border-primary" />
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-3">
                <button type="submit" data-testid="product-save-button"
                  className="flex items-center gap-2 bg-primary px-6 py-3 font-mono text-xs font-bold tracking-[0.2em] text-black transition-transform duration-300 hover:-translate-y-1">
                  <Plus size={14} /> {editingProduct ? "SAVE CHANGES" : "ADD PRODUCT"}
                </button>
                {editingProduct && (
                  <button type="button" data-testid="product-cancel-edit"
                    onClick={() => { setEditingProduct(null); setProductForm({ name: "", price: "", tag: "" }); }}
                    className="border border-white/25 px-6 py-3 font-mono text-xs tracking-[0.2em] text-white/70 hover:border-primary hover:text-primary">
                    CANCEL
                  </button>
                )}
              </div>
              <p className="font-mono text-[10px] tracking-[0.15em] text-white/30">PRICE CHANGES SYNC TO STRIPE CHECKOUT AUTOMATICALLY</p>
            </form>
            <div>
              {products.map((p) => (
                <div key={p.id} data-testid={`admin-product-row-${p.id}`} className="mb-3 flex flex-wrap items-center gap-3 border border-white/10 p-4">
                  {p.image ? (
                    <img src={p.image} alt={p.name} className="h-12 w-12 border border-white/15 object-cover" />
                  ) : (
                    <div className="flex h-12 w-12 items-center justify-center border border-white/15 bg-black font-anton text-lg text-primary">{p.name.charAt(0)}</div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-oswald font-bold tracking-wider text-white">{p.name}</p>
                    <p className="font-mono text-xs text-primary">${Number(p.price).toFixed(2)}</p>
                  </div>
                  <label data-testid={`product-photo-${p.id}`} className="flex cursor-pointer items-center gap-2 border border-white/25 px-3 py-2 font-mono text-[10px] tracking-[0.2em] text-white transition-colors duration-300 hover:border-primary hover:text-primary">
                    <Upload size={12} /> PHOTO
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0], `/admin/upload/product/${p.id}`)} />
                  </label>
                  <button data-testid={`product-edit-${p.id}`}
                    onClick={() => {
                      setEditingProduct(p.id);
                      setProductForm({ name: p.name, price: String(p.price), tag: p.tag || "" });
                      const ss = { S: "", M: "", L: "", XL: "", XXL: "" };
                      (p.sizes || []).forEach((s) => { if (s.size in ss) ss[s.size] = String(s.stock); });
                      setSizeStock(ss);
                    }}
                    className="border border-white/25 px-3 py-2 font-mono text-[10px] tracking-[0.2em] text-white transition-colors duration-300 hover:border-primary hover:text-primary">
                    EDIT
                  </button>
                  <button data-testid={`product-delete-${p.id}`} aria-label={`Delete ${p.name}`}
                    onClick={async () => { await api.delete(`/admin/products/${p.id}`).catch(() => {}); loadTab("MERCH"); }}
                    className="p-2 text-white/40 transition-colors duration-300 hover:text-red-400">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              {products.length === 0 && <p className="p-10 text-center font-mono text-xs text-white/40">NO PRODUCTS — ADD YOUR FIRST DROP</p>}
            </div>
          </div>
        )}

        {tab === "INBOX" && (
          <div data-testid="admin-inbox" className="border border-white/10">
            {messages.map((m) => (
              <div key={m.id} data-testid={`admin-message-${m.id.slice(0, 8)}`} className="flex flex-wrap items-start gap-4 border-b border-white/5 p-4">
                <span className="bg-primary/15 px-3 py-1 font-mono text-[10px] tracking-[0.25em] text-primary">{m.topic}</span>
                <div className="min-w-0 flex-1">
                  <p className="font-oswald font-bold tracking-wider text-white">{m.name}</p>
                  <p className="font-mono text-xs text-white/50">{m.email}</p>
                  <p className="mt-1 text-sm text-white/70">{m.message}</p>
                  <p className="mt-1 font-mono text-[10px] text-white/30">{String(m.created_at).slice(0, 10)}</p>
                </div>
                <button data-testid={`message-delete-${m.id.slice(0, 8)}`} aria-label={`Delete message from ${m.name}`}
                  onClick={async () => { await api.delete(`/admin/messages/${m.id}`).catch(() => {}); loadTab("INBOX"); }}
                  className="p-2 text-white/40 transition-colors duration-300 hover:text-red-400">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
            {messages.length === 0 && <p className="p-10 text-center font-mono text-xs text-white/40">INBOX EMPTY</p>}
          </div>
        )}

        {tab === "MEDIA" && (
          <div data-testid="admin-media" className="space-y-12">
            <div className="border border-primary/30 bg-black p-6">
              <h2 className="font-oswald text-xl font-bold tracking-wider text-white">SITE INTRO TRACK</h2>
              <p data-testid="intro-status" className="mt-1 font-mono text-[10px] tracking-[0.2em] text-white/40">
                {introInfo
                  ? `CURRENT: ${introInfo.title} — ${introInfo.enabled ? "PLAYS WHEN FANS ENTER" : "GATE DISABLED"}`
                  : "LOADING..."}
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <label data-testid="upload-intro" className="flex cursor-pointer items-center gap-2 bg-primary px-4 py-2 font-mono text-[10px] font-bold tracking-[0.2em] text-black transition-transform duration-300 hover:-translate-y-0.5">
                  <Upload size={12} /> SWAP INTRO
                  <input type="file" accept="audio/*" className="hidden" onChange={(e) => upload(e.target.files[0], "/admin/upload/intro")} />
                </label>
                <button
                  data-testid="intro-toggle-button"
                  onClick={async () => {
                    await api.post("/admin/settings/intro", { enabled: !(introInfo?.enabled ?? true) }).catch(() => {});
                    loadTab("MEDIA");
                  }}
                  className="border border-white/25 px-4 py-2 font-mono text-[10px] tracking-[0.2em] text-white transition-colors duration-300 hover:border-primary hover:text-primary"
                >
                  {introInfo?.enabled ? "TURN INTRO OFF" : "TURN INTRO ON"}
                </button>
              </div>
            </div>
            <div className="border border-primary/30 bg-black p-6">
              <h2 className="font-oswald text-xl font-bold tracking-wider text-white">LIVE CONCERT VIDEO (TOUR PAGE)</h2>
              <p data-testid="live-status" className="mt-1 font-mono text-[10px] tracking-[0.2em] text-white/40">
                {liveInfo
                  ? liveInfo.url
                    ? `${liveInfo.title} — ${liveInfo.enabled ? "LIVE ON TOUR PAGE" : "HIDDEN"}`
                    : "NO VIDEO YET — UPLOAD ONE"
                  : "LOADING..."}
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <label data-testid="upload-live-video" className="flex cursor-pointer items-center gap-2 bg-primary px-4 py-2 font-mono text-[10px] font-bold tracking-[0.2em] text-black transition-transform duration-300 hover:-translate-y-0.5">
                  <Upload size={12} /> UPLOAD VIDEO
                  <input type="file" accept="video/*" className="hidden" onChange={(e) => upload(e.target.files[0], "/admin/upload/live-video")} />
                </label>
                <button
                  data-testid="live-toggle-button"
                  onClick={async () => {
                    await api.post("/admin/settings/live", { enabled: !(liveInfo?.enabled ?? false) }).catch(() => {});
                    loadTab("MEDIA");
                  }}
                  className="border border-white/25 px-4 py-2 font-mono text-[10px] tracking-[0.2em] text-white transition-colors duration-300 hover:border-primary hover:text-primary"
                >
                  {liveInfo?.enabled ? "HIDE FROM TOUR PAGE" : "SHOW ON TOUR PAGE"}
                </button>
              </div>
              <p className="mt-3 font-mono text-[10px] tracking-[0.15em] text-white/30">MP4 / MOV / WEBM — 300MB MAX — UPLOADS GO STRAIGHT TO THE TOUR PAGE</p>
            </div>
            <div>
              <h2 className="font-oswald text-xl font-bold tracking-wider text-white">SITE GALLERY (GALLERY PAGE)</h2>
              <div className="mt-4 flex flex-wrap items-center gap-4">
                <label data-testid="upload-gallery-photo" className="flex cursor-pointer items-center gap-2 bg-primary px-4 py-2 font-mono text-[10px] font-bold tracking-[0.2em] text-black transition-transform duration-300 hover:-translate-y-0.5">
                  <Upload size={12} /> ADD PHOTO
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0], "/admin/upload/site-gallery")} />
                </label>
                <span data-testid="gallery-count" className="font-mono text-[10px] tracking-[0.2em] text-white/40">{galleryPhotos.length} PHOTOS LIVE</span>
              </div>
              <div className="mt-4 grid grid-cols-4 gap-3 md:grid-cols-6">
                {galleryPhotos.map((g) => (
                  <div key={g.id}>
                    <div className="group relative border border-white/10">
                      <img src={g.url} alt="" className="h-20 w-full object-cover" />
                      <button data-testid={`gallery-remove-${g.id.slice(0, 8)}`} aria-label="Remove photo"
                        onClick={async () => { await api.delete(`/admin/gallery/${g.id}`).catch(() => {}); loadTab("MEDIA"); }}
                        className="absolute right-1 top-1 bg-black/70 p-1 text-white/60 opacity-0 transition-opacity duration-300 hover:text-red-400 group-hover:opacity-100">
                        <Trash2 size={12} />
                      </button>
                    </div>
                    <input
                      data-testid={`gallery-caption-${g.id.slice(0, 8)}`}
                      defaultValue={g.caption || ""}
                      placeholder="Add caption..."
                      onBlur={async (e) => {
                        if (e.target.value !== (g.caption || "")) {
                          await api.put(`/admin/gallery/${g.id}`, { caption: e.target.value }).catch(() => {});
                          toast.success("Caption saved");
                          loadTab("MEDIA");
                        }
                      }}
                      className="mt-1 w-full border-b border-white/15 bg-transparent py-1 font-mono text-[10px] text-white/70 outline-none placeholder:text-white/25 focus:border-primary"
                    />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h2 className="mb-2 font-oswald text-xl font-bold tracking-wider text-white">TRACKS — UPLOAD REAL AUDIO & COVERS</h2>
              <p className="mb-6 font-mono text-[10px] tracking-[0.2em] text-white/40">MP3 / WAV / M4A / OGG FOR AUDIO — JPG / PNG / WEBP FOR COVERS — 50MB MAX</p>
              {tracks.map((t) => (
                <div key={t.id} data-testid={`admin-media-track-${t.id}`} className="mb-3 flex flex-wrap items-center gap-4 border border-white/10 p-4">
                  <img src={t.cover} alt={t.title} className="h-12 w-12 border border-white/15 object-cover" />
                  <span className="flex-1 font-oswald font-bold tracking-wider text-white">{t.title}</span>
                  <span className={`px-2 py-1 font-mono text-[10px] ${t.audio?.includes("/api/files/") ? "bg-primary/15 text-primary" : "bg-white/10 text-white/50"}`}>
                    {t.audio?.includes("/api/files/") ? "REAL AUDIO" : "DEMO AUDIO"}
                  </span>
                  <select
                    data-testid={`assign-artist-${t.id}`}
                    value={t.artist_id || ""}
                    onChange={async (e) => {
                      await api.post(`/admin/tracks/${t.id}/assign`, { artist_id: e.target.value }).catch(() => {
                        toast.error("Assign failed");
                      });
                      loadTab("MEDIA");
                    }}
                    className="border border-white/20 bg-transparent px-2 py-2 font-mono text-[10px] tracking-[0.15em] text-white/70 outline-none focus:border-primary"
                  >
                    <option value="" className="bg-black">UNASSIGNED</option>
                    {mediaArtists.map((a) => (
                      <option key={a.id} value={a.id} className="bg-black">{a.name}</option>
                    ))}
                  </select>
                  <label data-testid={`upload-audio-${t.id}`} className="flex cursor-pointer items-center gap-2 bg-primary px-4 py-2 font-mono text-[10px] font-bold tracking-[0.2em] text-black transition-transform duration-300 hover:-translate-y-0.5">
                    <Upload size={12} /> AUDIO
                    <input type="file" accept="audio/*" className="hidden" onChange={(e) => upload(e.target.files[0], `/admin/upload/track/${t.id}?kind=audio`)} />
                  </label>
                  <label data-testid={`upload-cover-${t.id}`} className="flex cursor-pointer items-center gap-2 border border-white/25 px-4 py-2 font-mono text-[10px] tracking-[0.2em] text-white transition-colors duration-300 hover:border-primary hover:text-primary">
                    <Upload size={12} /> COVER
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0], `/admin/upload/track/${t.id}?kind=cover`)} />
                  </label>
                </div>
              ))}
            </div>
            <div>
              <h2 className="mb-6 font-oswald text-xl font-bold tracking-wider text-white">MERCH — UPLOAD REAL PRODUCT PHOTOS</h2>
              {products.map((p) => (
                <div key={p.id} data-testid={`admin-media-product-${p.id}`} className="mb-3 flex flex-wrap items-center gap-4 border border-white/10 p-4">
                  <img src={p.image} alt={p.name} className="h-12 w-12 border border-white/15 object-cover" />
                  <span className="flex-1 font-oswald font-bold tracking-wider text-white">{p.name}</span>
                  <label data-testid={`upload-product-${p.id}`} className="flex cursor-pointer items-center gap-2 bg-primary px-4 py-2 font-mono text-[10px] font-bold tracking-[0.2em] text-black transition-transform duration-300 hover:-translate-y-0.5">
                    <Upload size={12} /> PHOTO
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0], `/admin/upload/product/${p.id}`)} />
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "SHOWS" && (
          <div data-testid="admin-shows" className="grid gap-10 lg:grid-cols-2">
            <form data-testid="admin-show-form" onSubmit={addShow} className="space-y-5 border border-white/10 bg-card p-8">
              <h2 className="font-oswald text-xl font-bold tracking-wider text-white">ADD A SHOW</h2>
              <input data-testid="show-date-input" type="date" required value={showForm.date}
                onChange={(e) => setShowForm({ ...showForm, date: e.target.value })}
                className="w-full border-b border-white/20 bg-transparent py-3 text-white outline-none [color-scheme:dark] focus:border-primary" />
              <input data-testid="show-city-input" placeholder="CITY, ST" required value={showForm.city}
                onChange={(e) => setShowForm({ ...showForm, city: e.target.value.toUpperCase() })}
                className="w-full border-b border-white/20 bg-transparent py-3 text-white outline-none placeholder:text-white/25 focus:border-primary" />
              <input data-testid="show-venue-input" placeholder="Venue" required value={showForm.venue}
                onChange={(e) => setShowForm({ ...showForm, venue: e.target.value })}
                className="w-full border-b border-white/20 bg-transparent py-3 text-white outline-none placeholder:text-white/25 focus:border-primary" />
              <select data-testid="show-status-select" value={showForm.status}
                onChange={(e) => setShowForm({ ...showForm, status: e.target.value })}
                className="w-full border-b border-white/20 bg-transparent py-3 text-white outline-none focus:border-primary">
                {["TICKETS", "SELLING FAST", "SOLD OUT", "ANNOUNCED"].map((s) => (
                  <option key={s} value={s} className="bg-black">{s}</option>
                ))}
              </select>
              <input data-testid="show-ticket-url-input" placeholder="Ticket link (optional, https://...)" value={showForm.ticket_url}
                onChange={(e) => setShowForm({ ...showForm, ticket_url: e.target.value })}
                className="w-full border-b border-white/20 bg-transparent py-3 text-white outline-none placeholder:text-white/25 focus:border-primary" />
              <input data-testid="show-time-input" placeholder="Show time (optional, e.g. 8:00 PM)" value={showForm.time}
                onChange={(e) => setShowForm({ ...showForm, time: e.target.value })}
                className="w-full border-b border-white/20 bg-transparent py-3 text-white outline-none placeholder:text-white/25 focus:border-primary" />
              <button type="submit" data-testid="show-add-button"
                className="flex items-center gap-2 bg-primary px-6 py-3 font-mono text-xs font-bold tracking-[0.2em] text-black transition-transform duration-300 hover:-translate-y-1">
                <Plus size={14} /> ADD SHOW
              </button>
            </form>
            <div>
              {shows.map((s) => (
                <div key={s.id} data-testid={`admin-show-row-${s.id}`} className="mb-3 flex items-center gap-4 border border-white/10 p-4">
                  <span className="font-mono text-xs text-primary">{s.date}</span>
                  <div className="min-w-0 flex-1">
                    <p className="font-oswald font-bold tracking-wider text-white">{s.city}</p>
                    <p className="font-mono text-[10px] text-white/40">{s.venue} — {s.status}{s.time ? ` · ${s.time}` : ""}</p>
                    <input
                      data-testid={`show-tix-${s.id}`}
                      defaultValue={s.ticket_url || ""}
                      placeholder="Ticket link (paste when on sale)..."
                      onBlur={async (e) => {
                        if (e.target.value !== (s.ticket_url || "")) {
                          await api.put(`/admin/shows/${s.id}`, { ticket_url: e.target.value }).catch(() => {});
                          toast.success("Ticket link saved — live on the tour page");
                          loadTab("SHOWS");
                        }
                      }}
                      className="mt-1 w-full border-b border-white/15 bg-transparent py-1 font-mono text-[10px] text-white/60 outline-none placeholder:text-white/25 focus:border-primary"
                    />
                  </div>
                  <label data-testid={`gallery-show-${s.id}`} className="flex cursor-pointer items-center gap-2 border border-white/25 px-3 py-2 font-mono text-[10px] tracking-[0.2em] text-white transition-colors duration-300 hover:border-primary hover:text-primary">
                    <Upload size={12} /> PHOTOS{s.photos?.length ? ` (${s.photos.length})` : ""}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0], `/admin/upload/show-gallery/${s.id}`)} />
                  </label>
                  <button data-testid={`show-delete-${s.id}`} onClick={() => delShow(s.id)} aria-label={`Delete ${s.city}`}
                    className="p-2 text-white/40 transition-colors duration-300 hover:text-red-400">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
